import { GenericResultModel } from "./base";

export class CompanyDetails {
  id: number = 0;
  name: string = "";
  displayName: string | null = null;
  tags: number[] = [];
  address: string | null = null;
  addition: string | null = null;
  city: string | null = null;
  zipcode: string | null = null;
  country: string | null = null;
  countryId: number | null = null;
  phone: string | null = null;
  peopleRoles: Array<{ id: number; role: string | null }> = [];

  constructor(data?: Partial<CompanyDetails>) {
    Object.assign(this, data);
    this.tags = data?.tags ?? [];
    this.peopleRoles = data?.peopleRoles ?? [];
  }
}

export class CompanyPerson {
  id: number = 0;
  firstname: string = "";
  lastname: string = "";
  phoneMobile: string | null = null;
  email: string | null = null;
  role: string | null = null;

  constructor(data?: Partial<CompanyPerson>) {
    Object.assign(this, data);
  }
}

export class CompanyProjectSummary {
  id: number = 0;
  nr: string = "";
  name: string = "";
  status: string | null = null;

  constructor(data?: Partial<CompanyProjectSummary>) {
    Object.assign(this, data);
  }
}

export class CompanyDetailItem {
  companyDetails: CompanyDetails = new CompanyDetails();
  persons: CompanyPerson[] = [];
  projects: CompanyProjectSummary[] = [];

  constructor(data?: Partial<CompanyDetailItem>) {
    this.companyDetails = new CompanyDetails(data?.companyDetails);
    this.persons = (data?.persons ?? []).map((person) => new CompanyPerson(person));
    this.projects = (data?.projects ?? []).map((project) => new CompanyProjectSummary(project));
  }
}

export class GetCompanyDetailsResultModel implements GenericResultModel {
  companyDetailItem: CompanyDetailItem = new CompanyDetailItem();
  resultText: string = "";
  resultData: string = "";
  resultDataInt?: number;
  resultItems: string[] = [];
  isValidationError: boolean = false;
  isAuthenticationError: boolean = false;
  isFatalError: boolean = false;
  hasError: boolean = false;
  errorCode: number = 0;
  feedback: string = "";
  performanceResults: Array<[number, string]> = [];

  constructor(data?: Partial<GetCompanyDetailsResultModel>) {
    Object.assign(this, data);
    this.companyDetailItem = new CompanyDetailItem(data?.companyDetailItem);
    this.resultItems = data?.resultItems ?? [];
    this.performanceResults = data?.performanceResults ?? [];
  }
}

export class ContactProjectRouteInfo {
  description: string = "";
  descriptionRouteType: string | null = null;
  descriptionRouteId: number | null = null;

  constructor(data?: Partial<ContactProjectRouteInfo>) {
    Object.assign(this, data);
  }
}

export class ContactProjectItem {
  id: number | null = null;
  projectId: number | null = null;
  projectName: string | null = null;
  displayName: string | null = null;
  name: string | null = null;
  nr: string | null = null;
  siteId: number | null = null;
  status: string | null = null;
  projectType: string | null = null;
  description: string | null = null;
  role: string | null = null;
  // The Project_Contact link's own id — set only when role is (i.e. a manual link).
  // Needed to call ContactService.removeProjectContact for this specific link.
  projectContactId: number | null = null;
  routingInfo: ContactProjectRouteInfo[] = [];
  project: any = null;

  constructor(data?: Partial<ContactProjectItem>) {
    Object.assign(this, data);
    this.routingInfo = (data?.routingInfo ?? []).map((route) => new ContactProjectRouteInfo(route));
  }
}

export class ProjectContactRouteInfo {
  description: string = "";
  taskNr: string | null = null;
  descriptionRouteType: string | null = null;
  descriptionRouteId: number | null = null;

  constructor(data?: Partial<ProjectContactRouteInfo>) {
    Object.assign(this, data);
  }
}

export class ProjectContactManualRole {
  projectContactId: number = 0;
  role: string | null = null;

  constructor(data?: Partial<ProjectContactManualRole>) {
    Object.assign(this, data);
  }
}

export class ProjectContactItem {
  id: number = 0;
  siteId: number | null = null;
  projectId: number | null = null;
  contactType: string = "";
  displayName: string = "";
  tags: number[] = [];
  routingInfo: ProjectContactRouteInfo[] = [];
  // One entry per manual link — a contact can hold several custom roles in the same project,
  // each editable and deletable on its own. Empty for contacts derived from other sources.
  manualRoles: ProjectContactManualRole[] = [];

  constructor(data?: Partial<ProjectContactItem>) {
    Object.assign(this, data);
    this.tags = data?.tags ?? [];
    this.routingInfo = (data?.routingInfo ?? []).map((route) => new ProjectContactRouteInfo(route));
    this.manualRoles = (data?.manualRoles ?? []).map((manual) => new ProjectContactManualRole(manual));
  }
}

export interface ContactRoleItem {
  id: number;
  name: string;
}

export class GetContactProjectsResultModel implements GenericResultModel {
  contactProjectItems: ContactProjectItem[] = [];
  resultText: string = "";
  resultData: string = "";
  resultDataInt?: number;
  resultItems: string[] = [];
  isValidationError: boolean = false;
  isAuthenticationError: boolean = false;
  isFatalError: boolean = false;
  hasError: boolean = false;
  errorCode: number = 0;
  feedback: string = "";
  performanceResults: Array<[number, string]> = [];

  constructor(data?: Partial<GetContactProjectsResultModel>) {
    Object.assign(this, data);
    this.contactProjectItems = (data?.contactProjectItems ?? []).map((item) => new ContactProjectItem(item));
    this.resultItems = data?.resultItems ?? [];
    this.performanceResults = data?.performanceResults ?? [];
  }
}
